import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import LoginCard from "./components/LoginCard";
import AppShell from "./layout/AppShell";
import HomePage from "./pages/HomePage";
import WorkflowsPage from "./pages/WorkflowsPage";
import RoadmapPage from "./pages/RoadmapPage";
import AdminPage from "./pages/AdminPage";
import AutomationBotsPage from "./pages/AutomationBotsPage";
import RunPage from "./pages/RunPage";
import ToolsPage from "./pages/ToolsPage";
import ToolDetailPage from "./pages/ToolDetailPage";
import ToolGroupPage from "./pages/ToolGroupPage";
import BuilderPage from "./pages/BuilderPage";
import { findTool } from "./toolRegistry";
import api from "./api";

// Legacy ?tab=<tool> deep links redirect into the new routes.
function LegacyTabRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab) {
      params.delete("tab");
      const rest = params.toString() ? `?${params.toString()}` : "";
      const path = tab === "adult-control" ? "/workflows/adult-control" : findTool(tab) ? `/tools/${tab}` : "/";
      navigate(path + rest, { replace: true });
    }
  }, [navigate]);
  return null;
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  // Until the first check answers, we do not know which of the two screens is
  // the right one. Assuming signed-out and correcting afterwards is what put a
  // sign-in card in front of people who were already signed in, and -- if they
  // clicked it before the answer arrived -- flashed the home page as the
  // browser left for the portal.
  const [checked, setChecked] = useState(false);

  // Check session status on app load and periodically
  useEffect(() => {
    const checkSessionStatus = async () => {
      try {
        const response = await api.get("/session-status");
        if (response.data.logged_in) {
          setIsLoggedIn(true);
          setUsername(response.data.user?.fullName || response.data.user?.username || "User");
        } else {
          setIsLoggedIn(false);
          setUsername(null);
        }
      } catch (error) {
        setIsLoggedIn(false);
        setUsername(null);
      } finally {
        setChecked(true);
      }
    };
    checkSessionStatus();
    const interval = setInterval(checkSessionStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsLoggedIn(false);
    setUsername(null);
  };

  if (!checked) {
    // Deliberately bare. This is on screen for one request now that the check
    // no longer builds a GIS, and a spinner that appears and vanishes reads
    // worse than a quiet moment.
    return <main className="min-h-screen" />;
  }

  if (!isLoggedIn) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center p-6">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo512.png" alt="CMAD" className="w-12 h-12" />
          <div>
            <h1 className="font-extrabold text-2xl leading-tight">Pioneer</h1>
            <p className="eyebrow">Consolidated Mosquito Abatement District</p>
          </div>
        </div>
        <LoginCard />
      </main>
    );
  }

  return (
    <BrowserRouter>
      <LegacyTabRedirect />
      <Routes>
        <Route path="/builder" element={<BuilderPage />} />
        <Route element={<AppShell username={username || "User"} onLogout={handleLogout} />}>
          <Route path="/" element={<HomePage username={username || "User"} />} />
          <Route path="/workflows" element={<WorkflowsPage />} />
          <Route path="/workflows/adult-control" element={<RunPage />} />
          <Route path="/tools" element={<ToolsPage username={username || undefined} />} />
          <Route path="/roadmap" element={<RoadmapPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/bots" element={<AutomationBotsPage />} />
          <Route path="/tools/group/:groupId" element={<ToolGroupPage />} />
          <Route path="/tools/:toolId" element={<ToolDetailPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
